import React from 'react'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    IconButton,
    List,
    ListItemButton,
    Stack,
    Typography
} from '@mui/material'

import { TravellerDocDetail } from '@/src/types/traveller'

import { TravellerDetailWrapper } from './travellerDetail'
import NoTravellerIcon from '@/src/assets/svgs/NoTraveller'

export interface TravellerListItemVm {
    id: number
    displayName: string
    initials: string
}

export interface TravellerDetailsVm {
    displayName: string
    initials: string
    gender: string
    email: string
    dateOfBirthValue: string
    phoneLabel: string
    documents: TravellerDocDetail[]
}

interface TravellersViewProps {
    t: (key: string) => string
    travellers: TravellerListItemVm[]
    activeTraveller: TravellerDetailsVm | null
    activeTravellerId: number | null
    onSelectTraveller: (id: number) => void
}

const TravellersEmptyState: React.FC<{ t: (key: string) => string; showPageTitle?: boolean }> = ({ t, showPageTitle = true }) => (
    <Box sx={{ maxWidth: 980, width: '100%' }}>
        {showPageTitle && (
            <Typography
                variant="h5"
                sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' }, lineHeight: 1.2, color: 'text.primary', mb: 2.5 }}
            >
                {t('title')}
            </Typography>
        )}

        <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', width: '100%' }}>
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 6, sm: 8 } }}>
                <Stack alignItems="center" spacing={1.25}>
                    <NoTravellerIcon sx={{ fontSize: { xs: 70, sm: 78 }, color: 'grey.400', mb: 1.25 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '18px', color: 'text.primary', textAlign: 'center' }}>
                        {t('noTravellers')}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>
                        {t('noTravellersSubtitle')}
                    </Typography>
                    <Button
                        disabled
                        variant="contained"
                        sx={{
                            mt: 1.75,
                            textTransform: 'none',
                            fontWeight: 600,
                            display: 'flex',
                            gap: '8px',
                            borderRadius: '8px',
                            px: '12px',
                            py: '15px'
                        }}
                    >
                        {t('addTraveller')}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    </Box>
)

export const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.25 }}>
            {value || '--'}
        </Typography>
    </Box>
)

const TravellersView: React.FC<TravellersViewProps> = ({
    t,
    travellers,
    activeTraveller,
    activeTravellerId,
    onSelectTraveller
}) => {
    return (
        <Box sx={{ maxWidth: 980 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 2, sm: 2.5 } }}>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' }, lineHeight: 1.3, color: 'text.primary' }}
                >
                    {t('title')}
                </Typography>

                <Button
                    disabled
                    variant="outlined"
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 14, borderRadius: 2, px: 1}}
                >
                    {t('add')}
                </Button>
            </Stack>

            <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                width: { xs: '100%', md: 260 },
                                maxHeight: 520,
                                overflowY: 'auto',
                                // Hide scrollbar (cross-browser)
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },

                                borderRight: { xs: 0, md: 1 },
                                borderColor: {md: 'divider'}
                            }}
                        >
                            <List disablePadding>
                                {travellers.map(item => {
                                    const isActive = activeTravellerId === item.id
                                    return (
                                        <ListItemButton
                                            key={item.id}
                                            onClick={() => onSelectTraveller(item.id)}
                                            selected={isActive}
                                            sx={{
                                                width: '240px',
                                                height: '70px',
                                                borderRadius: '8px',
                                                mb: 0.5,
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                gap: '10px',
                                                px: '12px',
                                                py: '15px',
                                                ...(isActive
                                                    ? {
                                                          bgcolor: 'grey.100',
                                                          '&:hover': { bgcolor: 'grey.100' }
                                                      }
                                                    : {})
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    bgcolor: 'grey.200',
                                                    color: 'text.secondary',
                                                    fontWeight: 700,
                                                    width: 36,
                                                    height: 36,
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {item.initials || <PersonOutlinedIcon sx={{ fontSize: 14 }} />}
                                            </Avatar>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    lineHeight: '20px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {item.displayName}
                                            </Typography>
                                        </ListItemButton>
                                    )
                                })}
                            </List>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {!activeTraveller ? (
                                <Box sx={{ py: 6 }}>
                                    <Typography sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('noTravellers')}</Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: 22,
                                                color: 'text.primary',
                                                lineHeight: 1.2,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                pt: 0.5
                                            }}
                                        >
                                            {activeTraveller.displayName}
                                        </Typography>

                                        <Stack direction="row" spacing={1}>
                                            <IconButton
                                                disabled
                                                aria-label={t('edit')}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                            <Box sx={{ width: '1px', height: '16px', bgcolor: 'divider', alignSelf: 'center' }} />
                                            <IconButton
                                                disabled
                                                aria-label={t('delete')}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    flexShrink: 0
                                                }}
                                            >
                                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: { xs: 2.5, sm: 3 } }}>
                                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 } }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <DetailItem label={t('gender')} value={activeTraveller.gender} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <DetailItem label={t('email')} value={activeTraveller.email} />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 } }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <DetailItem label={t('dateOfBirth')} value={activeTraveller.dateOfBirthValue} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <DetailItem label={t('mobileNumber')} value={activeTraveller.phoneLabel} />
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 2}} />
                                    <TravellerDetailWrapper t={t} documents={activeTraveller.documents} />
                                </>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}

export { TravellersView }
export { TravellersEmptyState }
export default TravellersView
export type { TravellersViewProps }

